import AsyncStorage from '@react-native-async-storage/async-storage';
import type {
  Account, Budget, Card, Goal, House, Income, Installment, InstallmentOccurrence,
  Invoice, OneOffExpense, Participant, RecurringExpense, RecurringIncome,
  RecurringOccurrence, MonthHistory,
} from '../types';

const CACHE_VERSION = 1 as const;

export interface CachedMonthData {
  invoices: Invoice[];
  recurringOccurrences: RecurringOccurrence[];
  incomes: Income[];
  installmentOccurrences: InstallmentOccurrence[];
  oneOffExpenses: OneOffExpense[];
  budgets: Budget[];
}

export interface FinanceCacheSnapshot {
  version: typeof CACHE_VERSION;
  savedAt: string;
  house: House | null;
  participants: Participant[];
  cards: Card[];
  recurringExpenses: RecurringExpense[];
  installments: Installment[];
  recurringIncomes: RecurringIncome[];
  accounts: Account[];
  goals: Goal[];
  months: Record<string, CachedMonthData>;
  history?: MonthHistory[];
}

function cacheKey(houseId: string) {
  return `@casa-em-dia/finance-cache/${houseId}/v${CACHE_VERSION}`;
}

export async function readFinanceCache(houseId: string): Promise<FinanceCacheSnapshot | null> {
  try {
    const raw = await AsyncStorage.getItem(cacheKey(houseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FinanceCacheSnapshot>;
    if (parsed.version !== CACHE_VERSION || !parsed.savedAt || !parsed.months) return null;
    return parsed as FinanceCacheSnapshot;
  } catch (error) {
    console.warn('[offline-cache] Could not read cached data:', error);
    return null;
  }
}

export async function writeFinanceCache(houseId: string, snapshot: FinanceCacheSnapshot): Promise<void> {
  try {
    await AsyncStorage.setItem(cacheKey(houseId), JSON.stringify(snapshot));
  } catch (error) {
    // Storage can be unavailable in private browsing or when the device is full.
    // The network path must continue working in that case.
    console.warn('[offline-cache] Could not save cached data:', error);
  }
}

export async function clearFinanceCache(houseId: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(cacheKey(houseId));
  } catch (error) {
    console.warn('[offline-cache] Could not clear cached data:', error);
  }
}
